'use client';
/**
 * Référentiel des habilitations — écran RQ (lot H4).
 *
 * Pourquoi cet écran existe : le référentiel, les niveaux et les attributions
 * n'étaient jusqu'ici modifiables qu'en base ou par l'API. Le RQ doit pouvoir
 * tout faire ici — décision du 29/07/2026.
 *
 * Deux partis pris :
 *  · une habilitation vise un AUTOMATE du parc ou une ACTIVITÉ sans automate.
 *    Le lien vers l'équipement est choisi dans la liste réelle, jamais saisi à
 *    la main : un code d'automate inventé décrirait une compétence sur une
 *    machine qui n'existe pas ;
 *  · « formation » n'apparaît PAS dans les niveaux proposés. C'est un état, pas
 *    un degré — l'accepter permettrait d'exiger « au moins en formation », ce
 *    qui viderait la règle de son sens. Il reste accordable à une personne.
 */
import React from 'react';
import { useGed } from '@/lib/store';
import type { RhHabilitation, RhHabilitationDetenue, RhPoste } from '@/lib/types';
import { useRemonteEnHaut, BoutonCreer, I, TitrePage, card, inputStyle, FilterBar, EmptyState, RefISO, ConfirmButton, Skeleton, Selecteur } from '@/components/ui';
import { ligneOuvrable, SansOuvrir } from '@/shared/Tableau';
import { AccesRefuseRh, Modale, lbl } from '@/components/screens/RhAbsences';
import { ParcoursIntegration, parcoursDe } from '@/components/screens/RhParcours';
import { RhGrillesEcran } from '@/components/screens/RhGrilles';
import type { Registre } from '@/components/screens/RhGrilles';
import { competencesDe, grillesDe, etatCompetences } from '@/lib/competences-habilitation';
import { RhParcoursSuivi } from '@/components/screens/RhParcoursSuivi';
import { RhPratiqueEcran } from '@/components/screens/RhPratique';

import { Bouton } from '@/shared/Bouton';
import { Segment } from '@/shared/Segment';
import { PiedActions } from '@/shared/PiedActions';
import { aujourdhuiIso } from '@/lib/meta';
/**
 * LA FICHE D'UNE HABILITATION — ce qu'il faut savoir pour l'obtenir.
 *
 * Demande de la RQ du 27/08/2026 : « en cliquant sur une habilitation, je veux
 * voir la liste des compétences qui lui sont rattachées… par niveau ».
 *
 * ── Rien n'est stocké ici ──────────────────────────────────────────────────
 *
 * Les compétences sont ASSEMBLÉES à la lecture depuis les grilles : chaque
 * tâche déclare les habilitations dont elle fait partie. Corriger une tâche
 * dans sa grille corrige toutes les fiches qui s'en servent, le jour même.
 * Recopier ces libellés ici les figerait.
 *
 * ⚠️ Le registre est chargé À L'OUVERTURE, pas au montage de l'écran : neuf
 * consultations sur dix du référentiel ne l'ouvrent jamais, et il porte
 * trois cents tâches.
 */
function FicheHabilitation({ h, postes, titulaires, peutModifier, onModifier, onFermer }: {
  h: RhHabilitation;
  postes: { code: string; libelle: string; habilitationsRequises?: (string | { code: string; niveauMin?: string | null })[] }[];
  titulaires: RhHabilitationDetenue[];
  peutModifier: boolean;
  onModifier: () => void;
  onFermer: () => void;
}) {
  const [reg, setReg] = React.useState<Registre | null>(null);
  const [erreur, setErreur] = React.useState('');
  React.useEffect(() => {
    let vivant = true;
    void fetch('/api/ged/ged/rh/grilles', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d) => { if (vivant) setReg(d as Registre); })
      /* Une erreur de chargement ne doit pas se lire « aucune compétence » :
         c'est exactement le faux vide que l'écran doit éviter. */
      .catch(() => { if (vivant) setErreur('Les grilles n’ont pas pu être lues : la liste ci-dessous serait fausse.'); });
    return () => { vivant = false; };
  }, []);

  /* LES EXIGENCES HORS PLANNING. Un poste n'est pas la seule façon d'exiger une habilitation :
     MoMento exige la sienne (`habilitationValidation`) pour valider un texte de compte rendu.
     Sans cette lecture, la fiche affirmait « elle ne bloque personne » d'une habilitation qui
     bloque la validation biologique du dépistage (RQ, 13/10/2026). `undefined` = lecture en cours ; `null` =
     illisible : dans les deux cas l'écran ne prétend pas qu'aucune exigence n'existe. */
  const [momentExige, setMomentExige] = React.useState<boolean | null | undefined>(undefined);
  React.useEffect(() => {
    let vivant = true;
    void fetch('/api/ged/ged/moment/referentiel', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d: { parametrage?: { habilitationValidation?: string | null } }) => { if (vivant) setMomentExige(String(d.parametrage?.habilitationValidation || '') === h.code); })
      .catch(() => { if (vivant) setMomentExige(null); });
    return () => { vivant = false; };
  }, [h.code]);

  const blocs = React.useMemo(() => competencesDe(reg?.grilles || [], h.code), [reg, h.code]);
  const etat = etatCompetences(blocs);
  const sources = grillesDe(blocs);
  /* Les postes qui l'exigent : c'est ce qui rend l'habilitation opposable.
     Une habilitation qu'aucun poste n'exige ne bloque personne au planning. */
  const exigeantes = postes.filter((p) => (p.habilitationsRequises || [])
    .some((e) => (typeof e === 'string' ? e : e.code) === h.code));

  return (
    <Modale titre={h.libelle} onFermer={onFermer}>
      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 14px', lineHeight: 1.6 }}>
        {h.code} · {TYPES[h.type] || h.type}
        {h.equipementNom ? ` · ${h.equipementNom}` : ''}
        {(h.disciplines || []).length ? ` · ${h.disciplines.join(', ')}` : ''}
        {(h.processus || []).length ? ` · ${(h.processus || []).join(', ')}` : ''}
        <br />
        Niveaux : <strong>{(h.niveaux || []).join(' → ') || '—'}</strong>
        {' · '}
        {titulaires.length
          ? <>{titulaires.length} titulaire{titulaires.length > 1 ? 's' : ''}</>
          : <span style={{ color: 'var(--amber-600)', fontWeight: 700 }}>aucun titulaire</span>}
      </p>

      {/* CE QUI REND L'HABILITATION OPPOSABLE — les postes qui l'exigent. */}
      <div style={{ ...card, padding: '12px 16px', marginBottom: 14 }}>
        <strong style={{ fontSize: 12.5, color: 'var(--text-strong)' }}>Exigée par</strong>
        <p style={{ fontSize: 12.5, margin: '4px 0 0', lineHeight: 1.55 }}>
          {[...exigeantes.map((p) => p.libelle), ...(momentExige ? ['MoMento — validation biologique du dépistage T21 (textes des comptes rendus et comptes rendus, T1 et T2)'] : [])].join(' · ')}
          {!exigeantes.length && momentExige === false && (
            <span style={{ color: 'var(--amber-600)' }}>
              Aucun poste ni MoMento n’exige cette habilitation : elle ne bloque personne.
              Cela peut être voulu — mais rien ne le dit.
            </span>
          )}
          {!exigeantes.length && momentExige === null && (
            <span style={{ color: 'var(--amber-600)' }}>
              Aucun poste n’exige cette habilitation au planning. Les exigences de MoMento n’ont pas pu être lues : cette fiche ne peut pas dire qu’elle ne bloque personne.
            </span>
          )}
        </p>
      </div>

      {erreur && (
        <div style={{ background: 'var(--amber-50)', border: '1px solid var(--amber-500)', borderRadius: 'var(--radius-md)', padding: 12, marginBottom: 14 }}>
          <strong style={{ fontSize: 12.5, color: 'var(--amber-600)' }}>⚠ {erreur}</strong>
        </div>
      )}

      {!reg && !erreur && <Skeleton rows={4} height={30} />}

      {reg && etat.message && (
        <div style={{ background: 'var(--amber-50)', border: '1px solid var(--amber-500)', borderRadius: 'var(--radius-md)', padding: 12, marginBottom: 14 }}>
          <p style={{ fontSize: 12.5, color: 'var(--text-body)', margin: 0, lineHeight: 1.55 }}>{etat.message}</p>
        </div>
      )}

      {reg && !etat.vide && (
        <>
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 10px', lineHeight: 1.5 }}>
            {etat.total} compétence{etat.total > 1 ? 's' : ''}, tirée{etat.total > 1 ? 's' : ''} de {sources.map((g) => `${g.libelle} (${g.n})`).join(' · ')}.
            {' '}Le degré est CUMULATIF : « pour » compte tout ce qu’il faut savoir à ce degré et en dessous.
          </p>
          {blocs.map((b) => (
            <div key={b.niveau} style={{ ...card, padding: 0, marginBottom: 10, overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', background: 'var(--gray-25)' }}>
                <strong style={{ flex: 1, fontSize: 13, color: 'var(--text-strong)' }}>{b.libelle}</strong>
                <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                  {b.competences.length} à ce degré · {b.cumul} pour l’atteindre
                </span>
              </div>
              {b.competences.length ? b.competences.map((c, i) => (
                <div key={c.code} style={{ padding: '9px 16px', borderTop: i ? '1px solid var(--border-subtle)' : '1px solid var(--border-default)' }}>
                  <div style={{ fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5 }}>{c.libelle}</div>
                  <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 2 }}>
                    {c.grilleLibelle}{c.section ? ` · ${c.section}` : ''}{c.methode ? ` · ${c.methode}` : ''}
                  </div>
                </div>
              )) : (
                /* Un degré non pourvu est un TROU DANS L'ÉCHELLE, pas une ligne
                   à masquer : exiger ce niveau sur un poste le rendrait
                   intenable, et il faut pouvoir le voir. */
                <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0, padding: '10px 16px', borderTop: '1px solid var(--border-default)' }}>
                  Aucune compétence n’est exigée à ce degré. L’exiger sur un poste le rendrait intenable.
                </p>
              )}
            </div>
          ))}
        </>
      )}

      <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
        {peutModifier && <Bouton variante="primaire" onClick={onModifier}>Modifier l’habilitation</Bouton>}
        <Bouton taille="sm" onClick={onFermer}>Fermer</Bouton>
      </div>
    </Modale>
  );
}

const TYPES: Record<string, string> = {
  automate: 'Automate',
  activite: 'Activité sans automate',
};

/** Pastille d'état d'une attribution. */
function Niveau({ niveau, origine }: { niveau: string; origine?: string }) {
  const formation = niveau === 'formation';
  /* Le niveau `partielle` a disparu de l'échelle le 17/08/2026 (routine /
     avancée / expert) et aucune détention n'en porte. La branche est retirée :
     un cas mort dans une pastille laisse croire qu'il peut se produire. */
  const fond = formation ? 'var(--amber-50)' : 'var(--green-50)';
  const trait = formation ? 'var(--amber-600)' : 'var(--green-600)';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: fond, color: trait,
      borderRadius: 'var(--radius-sm)', padding: '2px 8px', fontSize: 12.5, fontWeight: 700 }}>
      {niveau}
      {origine === 'anciennete' && (
        <span title="Accordée par ancienneté — aucune évaluation de compétence derrière"
          style={{ fontWeight: 400, opacity: 0.8 }}>· ancienneté</span>
      )}
    </span>
  );
}

export function RhHabilitationsEcran() {
  const {
    rhAcces, session, rhHabilitations, rhDetenues, rhPersonnel, equips, rhContrats, rhPostes,
    saveHabilitation, archiverHabilitation, accorderHabilitation, retirerHabilitation, rhChargement,
    selectedPersonne,
  } = useGed();

  /* ── ARRIVÉE PAR ADRESSE (25/08/2026) ─────────────────────────────────────
   * `selectedPersonne` porte l'`orgId` de quelqu'un : on vient de la matrice de
   * compétences, ou d'un lien partagé. L'onglet d'ouverture et la recherche en
   * DÉCOULENT — ils ne sont pas posés par un effet, ce qui éviterait mal un
   * rendu en cascade et surtout empêcherait de changer d'onglet ensuite : un
   * effet qui repose l'onglet à chaque rendu retiendrait l'utilisateur sur
   * « Par personne » quoi qu'il clique. */
  const [q, setQ] = React.useState('');
  /* Lot 5 du parcours (18/10/2026) : l'onglet « Habilitations par personne »
     est RETIRÉ — ce qu'il faisait (cocher, avis, décision, ouvrir un dossier)
     se fait sur le parcours de la personne. On arrive sur « Parcours en cours »,
     le tableau de bord ; une personne désignée ouvre directement son parcours. */
  const [onglet, setOnglet] = React.useState<'suivi' | 'referentiel' | 'parcours' | 'grilles' | 'pratique'>('suivi');
  const ouvrirDepuis = useGed((s) => s.ouvrirDepuis);
  // Un sous-onglet = une arrivée : on remonte (règle du 24/08/2026, cf. le crochet).
  useRemonteEnHaut(onglet);
  const [edite, setEdite] = React.useState<Partial<RhHabilitation> | null>(null);
  /* La fiche de lecture, distincte de l’éditeur : voir ce qu’exige une
     habilitation ne doit pas supposer le droit de la changer. */
  const [fiche, setFiche] = React.useState<RhHabilitation | null>(null);  /** L'habilitation qu'on s'apprête à archiver, et le motif — obligatoire. */
  const [aArchiver, setAArchiver] = React.useState<RhHabilitation | null>(null);
  const [motifArchive, setMotifArchive] = React.useState('');
  const [accord, setAccord] = React.useState<{ code?: string; orgId?: string; niveau?: string; origine?: string; dateObtention?: string; note?: string } | null>(null);

  /* Habilitations & formation : RQ et responsable technique, le RH en lecture
     (05/10/2026) — on suit la rubrique `rhhab`, plus le seul rôle `rh`. */
  if (!(useGed.getState().session.sections?.rhhab?.voir ?? rhAcces?.acces)) return <AccesRefuseRh />;
  const estRQ = session.roles.includes('rq');

  const qq = q.trim().toLowerCase();
  const vues = qq
    ? rhHabilitations.filter((h) => `${h.code} ${h.libelle} ${h.equipementNom || ''} ${(h.disciplines || []).join(' ')} ${(h.processus || []).join(' ')}`.toLowerCase().includes(qq))
    : rhHabilitations;

  // Combien de personnes détiennent chaque habilitation : une habilitation que
  // PERSONNE ne détient est un risque de continuité, pas un détail.
  const porteurs = new Map<string, RhHabilitationDetenue[]>();
  for (const d of rhDetenues) {
    if (!porteurs.has(d.code)) porteurs.set(d.code, []);
    porteurs.get(d.code)!.push(d);
  }
  const orphelines = rhHabilitations.filter((h) => h.actif !== false && !(porteurs.get(h.code) || []).length);

  // Combien de parcours restent à compléter : le chiffre est dans l'onglet, pas
  // seulement dans l'écran — sinon personne ne va voir.
  const aCompleter = rhPersonnel
    .filter((p) => p.statut !== 'sorti' && p.type !== 'externe')
    .filter((p) => parcoursDe(p, rhContrats, rhDetenues, rhPostes)
      .some((e) => !e.fait && !e.facultative)).length;

  return (
    <section>
      <TitrePage eyebrow={<>Plannings &amp; RH · <RefISO code="6.2" /></>} titre="Habilitations — référentiel"
        sousTitre={<>
          Une habilitation vise un automate du parc ou une activité sans automate. C’est elle — et les
          exigences portées par chaque poste — qui décide qui peut être planifié.
          Une habilitation accordée <strong>par ancienneté</strong> n’a aucune évaluation derrière elle :
          l’écran le dit, parce qu’un audit le demandera. <RefISO code="6.2" />
        </>} />

      {!!orphelines.length && (
        <div style={{ ...card, padding: 20, marginTop: 14, background: 'var(--amber-50)', border: '1px solid var(--amber-500)' }}>
          <strong style={{ fontSize: 13, color: 'var(--amber-600)' }}>
            {orphelines.length} habilitation(s) sans aucun titulaire
          </strong>
          <p style={{ fontSize: 12.5, margin: '4px 0 0', lineHeight: 1.5 }}>
            {orphelines.map((h) => h.libelle).join(' · ')}
            {/* ⚠️ LA PHRASE NE SUPPOSE PLUS QUE TOUTE HABILITATION PORTE UN
                AUTOMATE (relevé DAT-10 du 26/08/2026). « Un automate en service
                sans opérateur habilité est un constat d'audit » s'affichait
                pour TOUTES les orphelines, y compris « Gestion des urgences et
                de la garde », qui est explicitement une activité SANS automate.
                Un message qui parle d'autre chose que de ce qu'il désigne
                s'apprend vite à ne plus être lu. On ne le sert donc qu'aux
                habilitations qui portent réellement un équipement. */}
            {orphelines.some((h) => !!h.equipementNom) && (
              <><br />Un automate en service sans opérateur habilité est un constat d’audit.</>
            )}
            {orphelines.some((h) => !h.equipementNom) && (
              <><br />Une habilitation que personne ne détient ne protège rien : soit elle est à
              prononcer, soit elle est à archiver.</>
            )}
          </p>
        </div>
      )}

      <div style={{ margin: '18px 0 14px' }}>
        <Segment ariaLabel="Rubrique des habilitations" valeur={onglet} onChange={setOnglet}
          options={([['suivi', 'Parcours en cours'], ['parcours', `Accueil du jour J (${aCompleter})`], ['referentiel', `Référentiel (${rhHabilitations.length})`], ['grilles', 'Grilles de compétences'], ['pratique', `Maintien des compétences (${rhDetenues.length})`]] as const).map(([valeur, libelle]) => ({ valeur, libelle }))} />
      </div>

      {/* LA FICHE s'ouvre au clic sur une habilitation, pour TOUT LE MONDE.
          Jusqu'ici le clic ouvrait l'ÉDITEUR, et seulement pour le RQ : lire ce
          qu'exige une habilitation était donc réservé à qui a le droit de la
          changer. Modifier reste un bouton DANS la fiche. */}
      {fiche && (
        <FicheHabilitation
          h={fiche}
          postes={rhPostes}
          titulaires={porteurs.get(fiche.code) || []}
          peutModifier={estRQ}
          onModifier={() => { setEdite(fiche); setFiche(null); }}
          onFermer={() => setFiche(null)} />
      )}

      {onglet === 'pratique' ? (
        /* Le contrôle annuel de la pratique — la boucle que PROC-01 exige entre
           le planning et les habilitations, et que rien ne fermait. */
        <RhPratiqueEcran />
      /* ⚠️ L'ONGLET « COMPOSITION DES POSTES » A ÉTÉ RETIRÉ le 29/08/2026.
         Il n'existait que pour la garde, dont les tâches se choisissaient à la
         main à travers toutes les grilles. La RQ a tranché une règle plus simple :
         « pour tenir les gardes il faut être habilité sur tout le nécessaire des
         postes techniques de journée ». La garde se DÉRIVE donc comme les autres
         postes — l'union des exigences de P2 à P7, plus l'urgence — et la sélection
         à la main n'a plus d'objet. Le composant `RhPostesCompositionEcran` est
         conservé ; seule sa porte d'entrée est fermée. */
      ) : onglet === 'suivi' ? (
        // Lot 5 (18/10/2026) : les dossiers vivent sur le PARCOURS de chaque
        // personne (cocher, avis, décision) ; ici, le tableau de bord qui y mène.
        <RhParcoursSuivi />
      ) : onglet === 'grilles' ? (
        <RhGrillesEcran />
      ) : onglet === 'parcours' ? (
        <ParcoursIntegration onAllerHabilitations={(id) => ouvrirDepuis('rhhab', { view: 'parcours', cle: 'selectedPersonne', valeur: id })} />
      ) : onglet === 'referentiel' ? (
        <>
          {estRQ ? (
            <BoutonCreer style={{ marginBottom: 14 }}
              /* ⚠️ CE FORMULAIRE PRÉ-REMPLISSAIT DEUX VOCABULAIRES ABANDONNÉS
                 (corrigé le 29/08/2026) : `type: 'automate'` — les 28 habilitations
                 nommées d'après un automate ont été ARCHIVÉES le 25/08 et ne doivent
                 pas revenir — et `niveaux: ['partielle','complete']`, l'échelle à deux
                 degrés remplacée le 17/08 par routine / avancée / expert.
                 Une valeur par défaut n'est pas neutre : elle est ce que la plupart
                 des gens gardent. Créer une habilitation aujourd'hui la faisait donc
                 naître dans deux nomenclatures mortes, sans que rien ne le dise. */
              onClick={() => setEdite({ code: '', libelle: '', type: 'activite', disciplines: [], processus: [], niveaux: ['routine', 'avance', 'expert'], criteres: [], actif: true, ordre: 100 })}>
              Nouvelle habilitation
            </BoutonCreer>
          ) : (
            <p style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 14 }}>
              Consultable — définir ce qu’exige une compétence relève du RQ.
            </p>
          )}

          {rhHabilitations.length > 8 && (
            <FilterBar q={q} onQ={setQ} placeholder="Rechercher un automate, une activité, une discipline…"
              total={rhHabilitations.length} affiches={vues.length} onReset={() => setQ('')} />
          )}

          {!rhHabilitations.length && rhChargement > 0 ? (
            <div style={{ ...card, padding: 0, overflow: 'hidden' }}><Skeleton rows={6} /></div>
          ) : !vues.length ? (
            <EmptyState titre="Aucune habilitation" indice="Déclarez vos automates et vos activités." />
          ) : (
            <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
              {vues.map((h, i) => {
                const p = porteurs.get(h.code) || [];
                return (
                  /* Le clic ouvre l'habilitation ; « Supprimer » reste, sous
                     confirmation (Ines, 26/08/2026). */
                  <div key={h.id}
                    {...ligneOuvrable(() => setFiche(h), `Ouvrir ${h.libelle}`,
                      { display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px',
                        borderTop: i ? '1px solid var(--border-default)' : 'none' })}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-strong)' }}>
                        {h.libelle}
                        {h.actif === false && <span style={{ marginLeft: 8, fontSize: 12.5, color: 'var(--text-muted)' }}>(inactive)</span>}
                      </div>
                      <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 3 }}>
                        {h.code} · {TYPES[h.type] || h.type}
                        {h.equipementNom ? ` · ${h.equipementNom}` : ''}
                        {(h.disciplines || []).length ? ` · ${h.disciplines.join(', ')}` : ''}
                        {(h.processus || []).length ? ` · ${(h.processus || []).join(', ')}` : ''}
                        {' · niveaux : '}{(h.niveaux || []).join(' → ') || '—'}
                      </div>
                    </div>
                    <div style={{ flex: 'none', fontSize: 12.5, color: p.length ? 'var(--text-strong)' : 'var(--amber-600)', fontWeight: p.length ? 400 : 700 }}>
                      {p.length ? `${p.length} titulaire${p.length > 1 ? 's' : ''}` : 'personne'}
                    </div>
                    {/* ⚠️ « Archiver », et non « Supprimer » (relevé MAJ-09).
                        Un bouton rouge « Supprimer » sur chacune des 38 lignes
                        d'un registre OPPOSABLE, sans motif et sans conséquence
                        énoncée : le module des non-conformités applique depuis
                        toujours l'inverse à ce genre d'objet. */}
                    {estRQ && (
                      <SansOuvrir>
                        <Bouton taille="sm" onClick={() => { setAArchiver(h); setMotifArchive(''); }}>Archiver</Bouton>
                      </SansOuvrir>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      ) : null}

      {/* ── ARCHIVAGE : conséquences énoncées, motif exigé ─────────────────

           Le protocole des non-conformités, appliqué à un objet qui l'appelait

           autant : ce qui va se passer est écrit AVANT le clic, le motif fait au

           moins vingt caractères, rien n'est effacé, et le journal porte la

           décision. Les attributions détenues sont préservées : elles disent ce

           qui ÉTAIT vrai, et rien de vrai ne devient faux parce qu'on cesse

           d'attribuer. */}

      {aArchiver && (

        <Modale titre={`Archiver « ${aArchiver.libelle} »`} onFermer={() => setAArchiver(null)}>

          <div style={{ display: 'grid', gap: 12 }}>

            <div style={{ fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-body)' }}>

              L’habilitation sortira des sélecteurs et ne pourra plus être exigée par un poste.

              <strong> Les attributions déjà prononcées sont conservées</strong> — elles attestent

              qui était habilité, et à quelle date. Rien n’est supprimé, et la décision est portée

              au journal signé.

            </div>

            <div>

              <label style={lbl} htmlFor="hab-motif">Motif de l’archivage *</label>

              <textarea id="hab-motif" value={motifArchive} onChange={(e) => setMotifArchive(e.target.value)}

                rows={3} style={{ ...inputStyle, height: 'auto', padding: '10px 12px', resize: 'vertical' }} />

              <div style={{ fontSize: 12.5, color: motifArchive.trim().length >= 20 ? 'var(--text-muted)' : 'var(--amber-600)', marginTop: 3 }}>

                {motifArchive.trim().length >= 20

                  ? 'Ce motif sera relu dans six mois : il doit se suffire à lui-même.'

                  : `Au moins 20 caractères (${motifArchive.trim().length}). « Erreur » ou « doublon » n’expliquent rien.`}

              </div>

            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>

              <Bouton taille="sm" onClick={() => setAArchiver(null)}>Revenir</Bouton>

              <Bouton variante="primaire" disabled={motifArchive.trim().length < 20} onClick={() => void archiverHabilitation(aArchiver.id, motifArchive.trim()).then((ok) => { if (ok) setAArchiver(null); })}>

                Confirmer l’archivage

              </Bouton>

            </div>

          </div>

        </Modale>

      )}


      {edite && (
        <EditeurHabilitation
          h={edite} equipements={equips.filter((e) => e.categorie === 'automate' && e.code && e.nom)
            .map((e) => ({ code: String(e.code), nom: String(e.nom) }))}
          postes={rhPostes}
          onFermer={() => setEdite(null)}
          onEnregistrer={async (x) => { if (await saveHabilitation(x)) setEdite(null); }} />
      )}

      {accord && (
        <EditeurAttribution
          a={accord} habilitations={rhHabilitations} personnel={rhPersonnel}
          onFermer={() => setAccord(null)}
          onEnregistrer={async (x) => { if (await accorderHabilitation(x)) setAccord(null); }} />
      )}
    </section>
  );
}

/* ------------------------------- par personne ------------------------------ */

function EditeurHabilitation({ h, equipements, postes, onFermer, onEnregistrer }: {
  h: Partial<RhHabilitation>;
  equipements: { code: string; nom: string }[];
  postes: RhPoste[];
  onFermer: () => void;
  onEnregistrer: (h: Partial<RhHabilitation>) => void;
}) {
  const { settings, savePoste } = useGed();
  const [enPosteCode, setEnPosteCode] = React.useState<string | null>(null);
  // Le lien se pose des DEUX côtés (07/08/2026, Rh.tsx `FormulairePoste`) : le
  // poste porte `habilitationsRequises`, l'habilitation n'a pas de champ
  // réciproque. Basculer ici écrit donc directement sur le POSTE — pas sur `f`,
  // qui reste le brouillon de l'habilitation en cours d'édition.
  const basculerPoste = async (p: RhPoste) => {
    if (!h.code) return;
    const l = p.habilitationsRequises || [];
    const deja = l.some((e) => typeof e !== 'string' && e.code === h.code);
    const suivante = deja
      ? l.filter((e) => typeof e === 'string' || e.code !== h.code)
      : [...l, { code: h.code, niveauMin: null }];
    setEnPosteCode(p.code);
    await savePoste({ ...p, habilitationsRequises: suivante });
    setEnPosteCode(null);
  };
  const [f, setF] = React.useState<Partial<RhHabilitation>>(h);
  const maj = (k: keyof RhHabilitation, v: unknown) => setF((x) => ({ ...x, [k]: v }));
  // Compat un seul appareil (avant le 06/09/2026) : une fiche encore au
  // singulier se relit comme une liste à un élément, sans jamais la réécrire
  // tant que personne n'a rouvert et enregistré la fiche.
  const equipementCodesSel = f.equipementCodes || (f.equipementCode ? [f.equipementCode] : []);
  const toggleEquip = (code: string) => {
    const on = equipementCodesSel.includes(code);
    maj('equipementCodes', on ? equipementCodesSel.filter((c) => c !== code) : [...equipementCodesSel, code]);
    // Un critère qui pointait vers l'appareil retiré redevient transverse
    // plutôt que de disparaître : ce n'est pas la tâche qu'on efface.
    if (on) maj('criteres', (f.criteres || []).map((c) => (c.equipement === code ? { ...c, equipement: null } : c)));
  };
  const discs = (settings?.disciplines || []).filter((d) => d.active !== false).map((d) => d.name);
  // Deux listes, jamais fondues en une. Les neuf habilitations d'ACTIVITÉ du
  // laboratoire (tri, réception, stock, métrologie, validation biologique…)
  // étaient rangées sous de fausses disciplines « Qualité », « Métrologie » et
  // « Pré-analytique » : habiliter au tri des échantillons n'est pas habiliter
  // à une famille biologique. Elles portent désormais un processus.
  const procs = (settings?.processus || []).filter((p) => p.active !== false).map((p) => p.name);
  const niveaux = (f.niveaux || []).join(', ');

  return (
    <Modale titre={h.id ? 'Modifier une habilitation' : 'Nouvelle habilitation'} onFermer={onFermer}>
      <div style={{ display: 'grid', gap: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
          <label style={lbl}>Code
            <input style={{ ...inputStyle, width: '100%' }} value={f.code || ''}
              onChange={(e) => maj('code', e.target.value.toUpperCase())} placeholder="AUT-COBAS-E411" />
          </label>
          <label style={lbl}>Intitulé
            <input style={{ ...inputStyle, width: '100%' }} value={f.libelle || ''}
              onChange={(e) => maj('libelle', e.target.value)} placeholder="Cobas e411" />
          </label>
        </div>

        <label style={lbl}>Type
          <select style={{ ...inputStyle, width: '100%' }} value={f.type || 'automate'}
            onChange={(e) => maj('type', e.target.value)}>
            {Object.entries(TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>

        <div>
          <span style={lbl}>Automates du parc</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 4 }}>
            {equipements.map((e) => {
              const on = equipementCodesSel.includes(e.code);
              return (
                <Bouton key={e.code} taille="sm" variante={on ? 'primaire' : 'contour'} onClick={() => toggleEquip(e.code)} aria-pressed={on}>
                  {e.nom} <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>({e.code})</span>
                </Bouton>
              );
            })}
          </div>
          <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-muted)', marginTop: 5 }}>
            Choisis dans le parc réel — un code saisi à la main décrirait une machine qui n’existe pas.
            PLUSIEURS possibles : une habilitation d’activité couvre une paillasse entière (ACT-BIOCHIMIE
            en compte six).
          </span>
        </div>

        <div>
          <span style={lbl}>Critères, par appareil</span>
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '4px 0 8px', lineHeight: 1.5 }}>
            Chaque critère porte l’automate auquel il appartient — ce qui permet, sur une habilitation à
            plusieurs appareils, de regrouper les tâches propres à chacun. Un critère sans appareil reste
            transverse à toute la paillasse.
          </p>
          {[...equipementCodesSel.map((code) => ({ code, libelle: equipements.find((e) => e.code === code)?.nom || code })),
            { code: null as string | null, libelle: 'Transverse — sans appareil' }].map((grp) => (
            <GroupeCriteres key={grp.code ?? '__transverse__'} groupe={grp.code} libelle={grp.libelle}
              criteres={f.criteres || []}
              onAjouter={(texte) => maj('criteres', [...(f.criteres || []), { equipement: grp.code, texte }])}
              onRetirer={(i) => maj('criteres', (f.criteres || []).filter((_, k) => k !== i))} />
          ))}
        </div>

        <div>
          <span style={lbl}>Disciplines — familles biologiques</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 4 }}>
            {discs.map((d) => {
              const on = (f.disciplines || []).includes(d);
              return (
                <Bouton key={d} taille="sm" variante={on ? 'primaire' : 'contour'} aria-pressed={on} onClick={() => maj('disciplines', on
                  ? (f.disciplines || []).filter((x) => x !== d)
                  : [...(f.disciplines || []), d])}>{d}</Bouton>
              );
            })}
          </div>
        </div>

        <div>
          <span style={lbl}>Processus</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 4 }}>
            {procs.map((p) => {
              const on = (f.processus || []).includes(p);
              return (
                <Bouton key={p} taille="sm" variante={on ? 'primaire' : 'contour'} aria-pressed={on} onClick={() => maj('processus', on
                  ? (f.processus || []).filter((x) => x !== p)
                  : [...(f.processus || []), p])}>{p}</Bouton>
              );
            })}
          </div>
          <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-muted)', marginTop: 5 }}>
            Une habilitation d’activité relève d’un processus, pas d’une famille biologique : le tri
            des échantillons se pratique en biochimie comme en microbiologie.
          </span>
        </div>

        <label style={lbl}>Niveaux, du plus faible au plus fort
          <input style={{ ...inputStyle, width: '100%' }} value={niveaux}
            onChange={(e) => maj('niveaux', e.target.value.split(',').map((x) => x.trim()).filter(Boolean))}
            placeholder="partielle, complete" />
          <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-muted)', fontWeight: 400, marginTop: 4 }}>
            « formation » ne peut pas en faire partie : c’est l’état de quelqu’un qui apprend encore,
            pas un degré d’habilitation. Il reste accordable à une personne.
          </span>
        </label>

        {!!h.id && (
          <div>
            <span style={lbl}>Postes qui l’exigent</span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 4 }}>
              {postes.filter((p) => p.actif !== false).map((p) => {
                const on = (p.habilitationsRequises || []).some((e) => typeof e !== 'string' && e.code === h.code);
                return (
                  <Bouton key={p.code} taille="sm" variante={on ? 'primaire' : 'contour'} aria-pressed={on}
                    disabled={enPosteCode === p.code} onClick={() => void basculerPoste(p)}>
                    {p.libelle}
                  </Bouton>
                );
              })}
            </div>
            <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-muted)', marginTop: 5 }}>
              Chaque clic enregistre le poste aussitôt — ce n’est pas le bouton « Enregistrer » de
              cette fenêtre qui le fait, puisque l’exigence est portée par le poste et non par
              l’habilitation elle-même.
            </span>
          </div>
        )}

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <input type="checkbox" checked={f.actif !== false} onChange={(e) => maj('actif', e.target.checked)} />
          <span style={{ fontSize: 12.5 }}>Active</span>
        </label>
      </div>

      <PiedActions taille="sm" style={{ marginTop: 20 }} annuler={{ onClick: onFermer }} principale={{ libelle: "Enregistrer", onClick: () => onEnregistrer({ ...f, id: h.id }) }} />
    </Modale>
  );
}

/** Un groupe de critères, propres à un appareil (ou transverses, `groupe === null`).
 *  `criteres` reste le tableau COMPLET : l'index rendu au parent est celui de
 *  la fiche entière, pas celui, local, d'un sous-ensemble filtré. */
function GroupeCriteres({ groupe, libelle, criteres, onAjouter, onRetirer }: {
  groupe: string | null; libelle: string;
  criteres: { equipement: string | null; texte: string }[];
  onAjouter: (texte: string) => void;
  onRetirer: (index: number) => void;
}) {
  const [texte, setTexte] = React.useState('');
  const lignes = criteres
    .map((c, index) => ({ ...c, index }))
    .filter((c) => c.equipement === groupe);
  const ajouter = () => {
    const t = texte.trim();
    if (!t) return;
    onAjouter(t);
    setTexte('');
  };
  return (
    <div style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', padding: 10, marginBottom: 8 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 6 }}>
        {libelle}{lignes.length ? ` (${lignes.length})` : ''}
      </div>
      {!!lignes.length && (
        <ul style={{ margin: '0 0 8px', paddingLeft: 18, fontSize: 12.5, lineHeight: 1.6 }}>
          {lignes.map((c) => (
            <li key={c.index} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ flex: 1 }}>{c.texte}</span>
              <button type="button" aria-label={`Retirer « ${c.texte} »`}
                onClick={() => onRetirer(c.index)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 13, lineHeight: 1, padding: 0 }}>×</button>
            </li>
          ))}
        </ul>
      )}
      <div style={{ display: 'flex', gap: 6 }}>
        <input style={{ ...inputStyle, flex: 1 }} value={texte} onChange={(e) => setTexte(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); ajouter(); } }}
          placeholder="Nouveau critère…" />
        <Bouton taille="sm" type="button" onClick={ajouter} disabled={!texte.trim()}>
          <I n="plus" s={12} /> Ajouter
        </Bouton>
      </div>
    </div>
  );
}

export function EditeurAttribution({ a, habilitations, personnel, onFermer, onEnregistrer }: {
  a: { code?: string; orgId?: string; niveau?: string; origine?: string; dateObtention?: string; note?: string };
  habilitations: RhHabilitation[];
  personnel: { orgId: string; nom: string }[];
  onFermer: () => void;
  onEnregistrer: (x: Record<string, unknown>) => void;
}) {
  const [f, setF] = React.useState(a);
  const maj = (k: string, v: unknown) => setF((x) => ({ ...x, [k]: v }));
  const h = habilitations.find((x) => x.code === f.code);
  // « formation » s'ajoute aux niveaux du référentiel : c'est un état réel,
  // accordable, mais qui ne vaudra jamais habilitation à l'affectation.
  const niveaux = [...(h?.niveaux || []), 'formation'];
  const qui = personnel.find((p) => p.orgId === f.orgId);

  return (
    <Modale titre={`Accorder une habilitation${qui ? ` — ${qui.nom}` : ''}`} onFermer={onFermer}>
      <div style={{ display: 'grid', gap: 12 }}>
        <label style={lbl}>Habilitation
          <Selecteur style={{ ...inputStyle, width: '100%' }} value={f.code || ''}
            onChange={(v) => { maj('code', v); maj('niveau', ''); }}>
            <option value="">— choisir —</option>
            {habilitations.filter((x) => x.actif !== false).map((x) => (
              <option key={x.code} value={x.code}>{x.libelle}{x.equipementNom ? '' : ' (activité)'}</option>
            ))}
          </Selecteur>
        </label>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <label style={lbl}>Niveau
            <select style={{ ...inputStyle, width: '100%' }} value={f.niveau || ''}
              onChange={(e) => maj('niveau', e.target.value)} disabled={!h}>
              <option value="">— choisir —</option>
              {niveaux.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <label style={lbl}>Date d’obtention
            <input type="date" style={{ ...inputStyle, width: '100%' }} value={f.dateObtention || ''}
              onChange={(e) => maj('dateObtention', e.target.value)} />
          </label>
        </div>

        <label style={lbl}>Origine
          <select style={{ ...inputStyle, width: '100%' }} value={f.origine || 'evaluation'}
            onChange={(e) => maj('origine', e.target.value)}>
            <option value="evaluation">Évaluation de compétence</option>
            <option value="anciennete">Ancienneté (acquis de l’expérience)</option>
            <option value="declaration">Déclaration du responsable qualité</option>
          </select>
          <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-muted)', fontWeight: 400, marginTop: 4, lineHeight: 1.5 }}>
            {f.origine === 'anciennete'
              ? 'Aucune évaluation ne sera attachée. C’est une reprise d’historique assumée — en audit, l’écrire protège ; le taire expose.'
              : f.origine === 'declaration'
                ? 'Accordée sur décision du responsable qualité, sans évaluation formelle à ce jour. Écrivez le motif dans la note.'
                : 'Une évaluation a été conduite. Renseignez la preuve dans la note.'}
          </span>
        </label>

        <label style={lbl}>Note
          <textarea style={{ ...inputStyle, width: '100%', minHeight: 62 }} value={f.note || ''}
            onChange={(e) => maj('note', e.target.value)}
            placeholder="Référence du dossier d’évaluation, observations…" />
        </label>

        {f.niveau === 'formation' && (
          <p style={{ fontSize: 12.5, color: 'var(--amber-600)', margin: 0, lineHeight: 1.5 }}>
            « En formation » sera enregistré, mais <strong>ne permettra pas</strong> d’être planifié sur
            un poste qui exige cette habilitation.
          </p>
        )}
      </div>

      <PiedActions taille="sm" style={{ marginTop: 20 }} annuler={{ onClick: onFermer }} principale={{ libelle: "Accorder", disabled: !f.code || !f.niveau, onClick: () => onEnregistrer({ personnelOrgId: f.orgId, code: f.code, niveau: f.niveau,
            origine: f.origine || 'evaluation', dateObtention: f.dateObtention || '', note: f.note || '' }) }} />
    </Modale>
  );
}
